import { useCallback, useEffect, useRef, useState } from 'react'

import {
    CAPTURE_MODE_PROCESSED,
    MIC_MODE_AUTO,
    acquireDictationStream,
    forgetLearnedCapture,
    installDeviceChangeInvalidation,
    isSilentCapture,
    rememberLastUsedInputDevice,
    rememberLearnedCaptureMode,
} from './rambleMicCapture'
import { keepScreenAwake } from '../utils/screenWakeLock'

/**
 * One-shot mic recorder for the rambler dictation feature: start → talk → stop → a single
 * base64-encoded clip. Deliberately NOT the meeting-transcription pipeline (10s chunk recursion,
 * getDisplayMedia, hardcoded webm) — dictation needs only the microphone and one upload.
 *
 * It also verifies that the microphone actually produced sound (AT-2357). A macOS capture that
 * yields digital silence still encodes to a non-empty blob, so without that check the clip is
 * uploaded, billed, and comes back as "No speech detected" from a server that never had any
 * speech to find. See `rambleMicCapture.js` for the mechanism.
 */

export const RAMBLE_MAX_DURATION_SECONDS = 300
// The server rejects base64 payloads over 9M chars; 8MB of raw audio stays under that after the
// ~33% base64 inflation guard is applied server-side to the encoded string.
export const RAMBLE_MAX_AUDIO_BYTES = 8 * 1024 * 1024

// PT-5425: how long a detached take (stopped and promised to `onComplete`, its host already gone)
// may wait for MediaRecorder's `onstop` before the microphone is force-released and the take is
// reported lost. `onstop` normally arrives within one timeslice (1s).
export const DETACHED_DELIVERY_TIMEOUT_MS = 10000

// Never hardcode audio/webm: Safari/iOS records audio/mp4 and throws on a webm MediaRecorder.
const MIME_TYPE_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

export function pickSupportedMimeType() {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
        return undefined
    }
    return MIME_TYPE_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type))
}

export function isDictationSupported() {
    return (
        typeof MediaRecorder !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        !!navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function'
    )
}

/**
 * @param {{
 *   onComplete: (
 *     audio: { audioBase64: string, mimeType: string, durationSeconds: number, deviceLabel?: string },
 *   ) => void,
 *   onError: (
 *     code: 'permission-denied' | 'too-large' | 'not-supported' | 'recorder-error'
 *         | 'silent-input-retry' | 'silent-input',
 *     details?: { deviceLabel?: string, captureMode?: string, peak?: number, triedDeviceLabels?: string[] },
 *   ) => void,
 * }} options
 */
export default function useRambleRecorder({ onComplete, onError }) {
    const [isRecording, setIsRecording] = useState(false)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)

    const recorderRef = useRef(null)
    const streamRef = useRef(null)
    const chunksRef = useRef([])
    const timerRef = useRef(null)
    const cancelledRef = useRef(false)
    const startedAtRef = useRef(0)
    const mimeTypeRef = useRef('')
    const monitorRef = useRef(null)
    const levelTimerRef = useRef(null)
    const captureModeRef = useRef(CAPTURE_MODE_PROCESSED)
    const micModeSettingRef = useRef(MIC_MODE_AUTO)
    const deviceIdRef = useRef('')
    const deviceLabelRef = useRef('')
    const triedDevicesRef = useRef([])
    const releaseWakeLockRef = useRef(null)
    // PT-5424: a take stopped with `{ detach: true }` is still delivered after its host unmounts.
    const detachedRef = useRef(false)

    const onCompleteRef = useRef(onComplete)
    onCompleteRef.current = onComplete
    const onErrorRef = useRef(onError)
    onErrorRef.current = onError

    const cleanup = useCallback(() => {
        releaseWakeLockRef.current?.()
        releaseWakeLockRef.current = null
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        if (levelTimerRef.current) {
            clearInterval(levelTimerRef.current)
            levelTimerRef.current = null
        }
        if (monitorRef.current) {
            monitorRef.current.close()
            monitorRef.current = null
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
        recorderRef.current = null
        chunksRef.current = []
        setIsRecording(false)
        setElapsedSeconds(0)
    }, [])

    /**
     * `start()` is async — it waits on `getUserMedia` (a permission prompt the first time) and on
     * the AT-2357 silence probe, so several hundred milliseconds can pass between the caller
     * asking to record and a MediaRecorder existing. Push-to-talk releases inside that window all
     * the time (AT-2405), and before this latch `stop()`/`cancel()` found `recorderRef.current`
     * still null, did nothing at all, and the recorder then started AFTER the release with nobody
     * left to stop it — a hot microphone running to the 300s cap. The token is compared rather
     * than a bare boolean so a NEW start issued while an old acquisition is still in flight cannot
     * be aborted by the old one's release.
     */
    const startTokenRef = useRef(0)
    const abortedTokenRef = useRef(-1)
    const startingRef = useRef(false)

    const abortPendingStart = () => {
        if (!startTokenRef.current || recorderRef.current) return false
        abortedTokenRef.current = startTokenRef.current
        startingRef.current = false
        return true
    }

    /**
     * @param {{minDurationMs?: number}} [options] discard the take instead of delivering it when
     *   the recorder ran for less than this. Owned here because `startedAtRef` is the only honest
     *   measure of how much audio exists — the press duration is not, since acquisition eats an
     *   unpredictable slice of it.
     * @returns {boolean} whether a recording will actually be delivered to `onComplete`.
     */
    const stop = useCallback((options = {}) => {
        const { minDurationMs = 0, detach = false } = options
        // Released before the microphone ever opened: there is no audio, so this is a discard, not
        // a stop. Silent on purpose — the user held the button briefly and nothing happened.
        if (abortPendingStart()) return false
        const recorder = recorderRef.current
        if (!recorder || recorder.state === 'inactive') return false
        if (minDurationMs > 0 && Date.now() - startedAtRef.current < minDurationMs) {
            cancelledRef.current = true
            releaseWakeLockRef.current?.()
            releaseWakeLockRef.current = null
            recorder.stop()
            return false
        }
        releaseWakeLockRef.current?.()
        releaseWakeLockRef.current = null
        // `detach`: the caller is about to unmount this recorder (the add-task popup closes as soon
        // as a hold on the floating "+" is released, PT-5424) but still wants the take. The final
        // chunk and `onstop` arrive asynchronously, after that unmount, so the unmount cleanup
        // below must leave this take alone and let `onstop` deliver and clean up as usual.
        detachedRef.current = !!detach
        recorder.stop()
        return true
    }, [])

    const cancel = useCallback(() => {
        cancelledRef.current = true
        abortPendingStart()
        releaseWakeLockRef.current?.()
        releaseWakeLockRef.current = null
        const recorder = recorderRef.current
        if (recorder && recorder.state !== 'inactive') {
            recorder.stop()
        } else {
            cleanup()
        }
    }, [cleanup])

    const stopRef = useRef(stop)
    stopRef.current = stop

    /**
     * The live microphone level, 0..1, for the hold overlay's meter (AT-2408).
     *
     * Deliberately a GETTER rather than React state. The level has to be read ~10x a second to look
     * alive, and putting that in state would re-render every consumer of this hook ten times a
     * second — including `EditorToolbar`, which is a large tree. The meter instead polls this from
     * its own animation frame and drives `Animated` values, so the cost stays inside the meter.
     * Returns 0 when there is no monitor (no Web Audio) or no recording, which renders as a
     * resting meter rather than as an error — the meter is reassurance, never a gate.
     */
    const getInputLevel = useCallback(() => {
        const monitor = monitorRef.current
        if (!monitor || typeof monitor.getLevel !== 'function') return 0
        return monitor.getLevel()
    }, [])

    const start = useCallback(async () => {
        if (recorderRef.current || startingRef.current) return
        if (!isDictationSupported()) {
            onErrorRef.current?.('not-supported')
            return
        }

        // Acquisition owns the capture-mode decision: the user's setting, the remembered
        // workaround (and whether it still belongs to this device), and the pre-flight probe that
        // re-acquires a silent device with processing disabled BEFORE recording starts, so the
        // broken take never happens and no speech is lost. See rambleMicCapture.js.
        const startToken = startTokenRef.current + 1
        startTokenRef.current = startToken
        startingRef.current = true

        let acquired
        try {
            acquired = await acquireDictationStream({
                requestStream: audio => navigator.mediaDevices.getUserMedia({ audio }),
            })
        } catch (error) {
            if (startTokenRef.current === startToken) startingRef.current = false
            if (abortedTokenRef.current === startToken || startTokenRef.current !== startToken) return
            onErrorRef.current?.('permission-denied')
            return
        }
        const { stream, monitor, captureMode, setting, deviceId, deviceLabel, triedDevices, switchedDevice } = acquired

        // The caller let go (or cancelled) while the microphone was still opening. Hand the device
        // straight back instead of starting a recording nobody is holding — see the token comment
        // on `stop()`.
        if (abortedTokenRef.current === startToken || startTokenRef.current !== startToken) {
            if (startTokenRef.current === startToken) startingRef.current = false
            monitor?.close()
            stream.getTracks().forEach(track => track.stop())
            return
        }

        const mimeType = pickSupportedMimeType()
        let recorder
        try {
            recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
        } catch (error) {
            startingRef.current = false
            monitor?.close()
            stream.getTracks().forEach(track => track.stop())
            onErrorRef.current?.('not-supported')
            return
        }

        monitorRef.current = monitor
        captureModeRef.current = captureMode
        micModeSettingRef.current = setting
        deviceIdRef.current = deviceId
        deviceLabelRef.current = deviceLabel
        triedDevicesRef.current = triedDevices || []
        // Diagnostic only: the Settings picker shows this so "System default" is not a black box.
        // Which microphone the browser hands us is otherwise only visible in the DevTools console.
        rememberLastUsedInputDevice({ deviceId, label: deviceLabel })
        if (switchedDevice) {
            // Worth a line of its own: from here on the recording is NOT coming from the microphone
            // the browser would have chosen, which is the single most confusing thing about this
            // failure class when it is reported.
            console.log('[rambler] switched input device', { deviceLabel, deviceId, tried: triedDevices })
        }
        cancelledRef.current = false
        detachedRef.current = false
        chunksRef.current = []
        mimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm'
        streamRef.current = stream
        recorderRef.current = recorder
        startedAtRef.current = Date.now()

        recorder.ondataavailable = event => {
            if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
        }
        recorder.onerror = () => {
            cancelledRef.current = true
            releaseWakeLockRef.current?.()
            releaseWakeLockRef.current = null
            onErrorRef.current?.('recorder-error')
            if (recorder.state !== 'inactive') recorder.stop()
            else cleanup()
        }
        recorder.onstop = () => {
            const wasCancelled = cancelledRef.current
            const durationSeconds = Math.round((Date.now() - startedAtRef.current) / 1000)
            const chunks = chunksRef.current
            const recordedMimeType = mimeTypeRef.current
            // Read the peak BEFORE cleanup tears the analyser down; `null` means we could not
            // measure at all (no Web Audio), which never blocks the upload.
            const peak = monitorRef.current ? monitorRef.current.sample() : null
            const usedCaptureMode = captureModeRef.current
            const usedMicModeSetting = micModeSettingRef.current
            const usedDeviceId = deviceIdRef.current
            const deviceLabel = deviceLabelRef.current
            const triedDeviceLabels = (triedDevicesRef.current || []).map(device => device.label).filter(Boolean)
            cleanup()
            if (wasCancelled) return

            const blob = new Blob(chunks, { type: recordedMimeType })
            if (!blob.size) {
                onErrorRef.current?.('recorder-error')
                return
            }
            // Diagnostics for the next report of this class: a silent take is obvious here and
            // nowhere else (the blob is non-empty and the server error is generic).
            console.log('[rambler] capture', {
                micModeSetting: usedMicModeSetting,
                captureMode: usedCaptureMode,
                peak,
                deviceLabel,
                deviceId: usedDeviceId,
                triedDeviceLabels,
                blobBytes: blob.size,
                durationSeconds,
            })
            if (isSilentCapture(peak)) {
                // Never upload silence: it costs Gold and comes back as "No speech detected".
                const details = { deviceLabel, captureMode: usedCaptureMode, peak, triedDeviceLabels }
                // An explicit user setting is reported, never overridden — the whole point of the
                // setting is that the automatic path stops making decisions for them.
                if (usedMicModeSetting !== MIC_MODE_AUTO) {
                    onErrorRef.current?.('silent-input', details)
                } else if (usedCaptureMode === CAPTURE_MODE_PROCESSED) {
                    // The probe let this through (it went dead mid-take, or its noise floor was
                    // non-zero); the completed take is the stronger signal, so learn from it.
                    rememberLearnedCaptureMode({ deviceId: usedDeviceId, deviceLabel })
                    onErrorRef.current?.('silent-input-retry', details)
                } else {
                    // Raw capture is silent too, so neither the remembered mode nor the remembered
                    // device is the answer here — drop both rather than keeping a degraded, wrong
                    // configuration forever. The next recording starts from a clean slate.
                    forgetLearnedCapture()
                    onErrorRef.current?.('silent-input', details)
                }
                return
            }
            if (blob.size > RAMBLE_MAX_AUDIO_BYTES) {
                onErrorRef.current?.('too-large')
                return
            }
            const reader = new FileReader()
            reader.onloadend = () => {
                if (typeof reader.result === 'string' && reader.result.length > 0) {
                    onCompleteRef.current?.({
                        audioBase64: reader.result,
                        mimeType: recordedMimeType,
                        durationSeconds,
                        // Carried through so a server-side "no speech" can name the microphone it
                        // actually listened to: a device that is alive but WRONG passes every local
                        // check — it records the room while the user talks into another mic.
                        deviceLabel,
                    })
                } else {
                    onErrorRef.current?.('recorder-error')
                }
            }
            reader.readAsDataURL(blob)
        }

        // 1s timeslices so a mid-recording crash loses at most a second, and Safari (which is
        // unreliable about a single final dataavailable) still delivers steady chunks.
        try {
            recorder.start(1000)
        } catch (error) {
            startingRef.current = false
            cleanup()
            onErrorRef.current?.('recorder-error')
            return
        }
        startingRef.current = false
        releaseWakeLockRef.current = keepScreenAwake()
        setIsRecording(true)
        setElapsedSeconds(0)
        // Sampled far faster than the 1s UI tick: the analyser only ever holds ~43ms of audio, so
        // a slow poll would step over the gaps between words and under-report the peak.
        if (monitorRef.current) {
            levelTimerRef.current = setInterval(() => {
                monitorRef.current?.sample()
            }, 100)
        }
        timerRef.current = setInterval(() => {
            setElapsedSeconds(previous => {
                const next = previous + 1
                if (next >= RAMBLE_MAX_DURATION_SECONDS) stopRef.current()
                return next
            })
        }, 1000)
    }, [cleanup])

    // Plugging in headphones or switching the system default retires a learned workaround, so the
    // capture mode follows the hardware instead of outliving it.
    useEffect(() => installDeviceChangeInvalidation(), [])

    useEffect(() => {
        return () => {
            const pending = recorderRef.current
            if (detachedRef.current && pending && pending.state === 'inactive' && !cancelledRef.current) {
                // A detached take is between `stop()` and `onstop`: only the UI timers go now,
                // `onstop` releases the stream and monitor after it has read them.
                if (timerRef.current) clearInterval(timerRef.current)
                if (levelTimerRef.current) clearInterval(levelTimerRef.current)
                timerRef.current = null
                levelTimerRef.current = null
                // Safety net (PT-5425): if `onstop` never arrives the microphone must not stay hot,
                // and the host has to learn the take is gone so it can restore its draft.
                setTimeout(() => {
                    if (recorderRef.current !== pending || cancelledRef.current) return
                    cancelledRef.current = true
                    if (monitorRef.current) monitorRef.current.close()
                    monitorRef.current = null
                    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop())
                    streamRef.current = null
                    recorderRef.current = null
                    onErrorRef.current?.('recorder-error')
                }, DETACHED_DELIVERY_TIMEOUT_MS)
                return
            }
            cancelledRef.current = true
            abortedTokenRef.current = startTokenRef.current
            releaseWakeLockRef.current?.()
            releaseWakeLockRef.current = null
            const recorder = recorderRef.current
            if (recorder && recorder.state !== 'inactive') recorder.stop()
            if (timerRef.current) clearInterval(timerRef.current)
            if (levelTimerRef.current) clearInterval(levelTimerRef.current)
            if (monitorRef.current) monitorRef.current.close()
            if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop())
        }
    }, [])

    return { isRecording, elapsedSeconds, start, stop, cancel, getInputLevel }
}
